// Abre en otra pestaña un documento HTML que sirve la API (BUG-1090, `ui/MG-ILE-1090.md`).
//
// POR QUÉ NO UN ENLACE. Un `<a href={api} target="_blank">` hace una navegación del navegador, y
// esa navegación no lleva cabeceras propias: con el inicio de sesión de Microsoft el token vive en
// la memoria de MSAL, no en una cookie, así que la API recibía la petición sin `Authorization` y
// la pestaña mostraba el 401. Aquí el documento se pide por el cliente (`pedirBinario`, que pone
// el token en un solo punto) y la pestaña recibe una URL `blob:` con lo que llegó.
//
// LA PESTAÑA SE ABRE EN EL CLIC. `window.open` va ANTES del primer `await`: abierta después de
// esperar la descarga, el navegador la trataría como ventana emergente no pedida y la bloquearía.
// Es el mecanismo que ya usaba «Exportar PDF» de la Trazabilidad, ahora en un solo lugar.

/** Sin pestaña nueva no hay dónde mostrar el documento. */
export const TEXTO_VENTANA_BLOQUEADA =
  'El navegador no dejó abrir la pestaña del documento. Permite las ventanas de este sitio y vuelve a intentarlo.';

/**
 * La URL `blob:` se revoca pasado este plazo: la pestaña ya tiene el documento cargado y un
 * minuto sobra. Recargar esa pestaña después la deja en blanco; imprimir no recarga (aceptado
 * por el operador, 7-oct-2026).
 */
const PLAZO_DE_LA_URL_MS = 60_000;

/** El navegador no dejó abrir la pestaña: no se descargó nada. */
export class VentanaBloqueada extends Error {
  constructor() {
    super(TEXTO_VENTANA_BLOQUEADA);
    this.name = 'VentanaBloqueada';
  }
}

/**
 * Un `blob:` sin tipo HTML el navegador lo descarga en vez de pintarlo. El servidor responde
 * `text/html; charset=utf-8`, pero el tipo puede perderse por el camino (un proxy, un doble de
 * pruebas): se rehace con el tipo correcto y el mismo contenido.
 */
function comoHtml(documento: Blob): Blob {
  if (documento.type.startsWith('text/html')) return documento;
  return new Blob([documento], { type: 'text/html;charset=utf-8' });
}

/**
 * Abre la pestaña, descarga el documento con `descargar` y se lo entrega a la pestaña.
 *
 * `descargar` solo se llama si la pestaña se abrió: quien la pasa puede marcar ahí su estado de
 * «descargando», y si el navegador bloquea la ventana no sale ninguna petición. Si la descarga
 * falla la pestaña se cierra (una pestaña en blanco no dice nada) y el error sube tal cual, para
 * que la pantalla lo muestre con `aFallo`.
 *
 * @throws {VentanaBloqueada} si `window.open` devuelve `null`.
 */
export async function abrirDocumentoEnPestana(descargar: () => Promise<Blob>): Promise<void> {
  const ventana = window.open('', '_blank');
  if (!ventana) throw new VentanaBloqueada();
  // Sin `opener`, el documento no puede tocar la pestaña de la aplicación.
  ventana.opener = null;
  try {
    const url = URL.createObjectURL(comoHtml(await descargar()));
    ventana.location.href = url;
    globalThis.setTimeout(() => URL.revokeObjectURL(url), PLAZO_DE_LA_URL_MS);
  } catch (error) {
    ventana.close();
    throw error;
  }
}
